import React, {useEffect, useState} from "react";
import {
    Card,
    Page,
    Text,
    Layout,
    Button,
    ResourceItem,
    ResourceList,
    ResourceListProps,
    Tag, ButtonGroup, Combobox, BlockStack, InlineStack, List, Spinner, Banner
} from "@shopify/polaris";
import Alert from "../Lib/Alert";
import Http from "../Lib/Http";
import {ResourceChart as Chart} from "../types";
import {Button as ModalButton, Modal, Redirect} from "@shopify/app-bridge/actions";
import {useGoTo} from "@admin/Lib/useGoTo";
import Menu from "@admin/Lib/Menu";
import CriticalError from "@admin/components/CriticalError";


function Help() {
    const [charts, setCharts] = useState<Chart[]>([]);
    const [selectedChart, setSelectedChart] = useState<string | null>(null);
    const [cancelConfirmed, setCancelConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectError, setSelectError] = useState(false);
    const [chartCount, setChartCount] = useState(0);
    const [free, setFree] = useState<number>(1);
    const goTo = useGoTo();
    const [error, setError] = useState(false);

    const [selectedItems, setSelectedItems] = useState<
        ResourceListProps['selectedItems']
    >([]);


    useEffect(() => {
        getShops();
    }, []);


    const getShops = () => {

        Http.get(`/app/api/v1/shop.json?charts=1`)
            .then((response) => {
                let shop = response.data.shop
                setChartCount(response.data.charts);
                setFree(shop.free);
                Menu.loadMenu(shop);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            });
    };



    const openSupport = () => {
        window.open('mailto:support@eastsideco.com', '_blank');
    };

    const fetchCharts = () => {
        setLoading(true);

        Http.get(`/app/api/v1/charts.json?all=1`, )
            .then((response) => {
                const data = response.data.charts;
                setCharts(data);
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                console.error('Error fetching data:', error);
            });
    };


    const cancelPlan = () => {

        let confirmButtonSend = ModalButton.create(window.app, {label: 'Continue'});
        confirmButtonSend.subscribe(ModalButton.Action.CLICK, () => {
            confirmCancelModal.dispatch(Modal.Action.CLOSE);
            if (chartCount > 3) {
                fetchCharts();
                setCancelConfirmed(true);
            } else {
                cancelPlanConfirmed();
            }
        });

        let cancelButton = ModalButton.create(window.app, {label: 'Cancel'});

        cancelButton.subscribe(ModalButton.Action.CLICK, () => {
            confirmCancelModal.dispatch(Modal.Action.CLOSE);
        });

        let confirmCancelModal = Modal.create(window.app, {
            title: 'cancellation of premium plan',
            message: 'Please click continue if you would like to cancel your premium plan, as a result you will immediately lose access to all premium feature including some size guides.',
            footer: {
                buttons: {
                    primary: confirmButtonSend,
                    secondary: [cancelButton],
                },
            },
        });
        confirmCancelModal.dispatch(Modal.Action.OPEN);
    };



    const cancelPlanConfirmed = () => {
           if(selectedItems && selectedItems.length > 3){
                return;
           }
        setError(false);
        setLoading(true);
        Http.post('/app/api/v1/billing/downgrade', {charts:selectedItems})
            .then((res) => {
                let redirect = Redirect.create(window.app);
                redirect.dispatch(Redirect.Action.APP, '/app');
            })
            .catch((e) => {
                setLoading(false);
                setError(true);
                console.log('[Esc-Size-Guide] Failed to send reattach-selector', {e});
            });
    };


    return (
        <Page title="Help and Support" backAction={{content: 'Home', onAction() {goTo('')}}}>
            {error &&
                <Layout>
                    <Layout.Section>
                        <CriticalError  onDismiss={() => {setError(false)}}  showAlert={error} title={'There was an error cancelling your plan'} />
                    </Layout.Section>
                </Layout>
            }
            <Layout>
                {!cancelConfirmed &&
                    <Layout.Section>
                        <Card roundedAbove="sm">
                            <BlockStack gap="200">
                                <Text variant="headingMd" as="h6">
                                    Help
                                </Text>
                                <BlockStack gap="200">
                                    If you need help setting ESC - Size Charts & Size Guide or have any questions about the app,
                                    please contact our friendly support team at <span style={{fontStyle:'italic', fontWeight:'bold'}}>support@eastsideco.com</span>
                                </BlockStack>
                                <InlineStack align="end">
                                    <ButtonGroup>
                                        <Button
                                            variant="primary"
                                            onClick={openSupport}
                                            accessibilityLabel="open Supportr"
                                        >
                                            Contact support
                                        </Button>
                                    </ButtonGroup>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    </Layout.Section>
                }
                {(!cancelConfirmed && free == 0) &&
                    <Layout.Section>
                        <Card roundedAbove="sm">
                            <BlockStack gap="200">
                                <Text variant="headingMd" as="h6">
                                    You are on the Premium Plan for ESC - Size Charts & Size Guide
                                </Text>
                                <p>
                                    <Button onClick={cancelPlan} variant={"plain"}>Click here</Button>   to cancel your plan if you'd like to lose access to the following features:

                                </p>

                                <List>
                                    <List.Item>Unlimited size charts and guides.</List.Item>
                                    <List.Item>Importing charts</List.Item>
                                    <List.Item>Exporting charts</List.Item>
                                    <List.Item>Advanced design settings</List.Item>
                                </List>
                                <BlockStack inlineAlign="start">
                                    If you cancel your premium plan, you will only be able to have 3 active size charts at any one time
                                </BlockStack>
                            </BlockStack>
                        </Card>
                    </Layout.Section>
                }
                {cancelConfirmed && !loading && (
                    <Layout.Section>
                        {(selectedItems && selectedItems.length > 3) &&
                            <div className="mb-20">
                                <Banner
                                    title="Please select three charts"
                                    tone="warning"
                                >
                                    <p>
                                        In order to proceed with cancellation of premium plan, please only select 3 charts to retain on the free plan.                                    </p>
                                </Banner>
                            </div>

                        }
                        <Card roundedAbove="sm">
                            <Text variant="headingMd" as="h6">
                                Select three size guides to retain
                            </Text>
                            <div className="mt-10">
                                <p>
                                    You currently have more than three size guides. You can select up to three size
                                    guides that you would like to retain on the free plan and then press <strong>'Confirm
                                    Cancellation'</strong> below to complete your plan downgrade. You will be locked out
                                    of all other size guides whilst on the free plan.
                                </p>
                            </div>

                            <div className="mt-20 mb-20">
                                <ResourceList
                                    selectable={true}
                                    selectedItems={selectedItems}
                                    onSelectionChange={setSelectedItems}
                                    resourceName={{singular: 'chart', plural: 'charts'}}
                                    items={charts}
                                    renderItem={(item) => {
                                        const {id, title, tag} = item;
                                        const tagsArray = tag.split(',').filter(tag => tag.trim() !== '');
                                        return (
                                            <ResourceItem
                                                onClick={() => {
                                                    setSelectedChart(id)
                                                }}
                                                id={id}
                                                accessibilityLabel={`View details for ${title}`}
                                                name={title}
                                            >
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}>
                                                    <div>
                                                        <Text variant="headingSm" as="h6">{title}</Text>
                                                        <div className='tag-container'>
                                                            {tagsArray.map((tag, index) => (
                                                                <Tag key={index}>{tag.trim()}</Tag>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </ResourceItem>
                                        );
                                    }}
                                />
                            </div>
                            <InlineStack align="end">
                                <ButtonGroup>
                                    <Button
                                        variant="primary"
                                        onClick={cancelPlanConfirmed}
                                        accessibilityLabel="Add tracking number"
                                    >
                                        Confirm Cancellation
                                    </Button>
                                </ButtonGroup>
                            </InlineStack>


                        </Card>
                    </Layout.Section>
                )}

                {loading && (
                    <Layout.Section>
                        <Spinner accessibilityLabel="Loading" size="large"/>
                    </Layout.Section>
                )}
            </Layout>
        </Page>
    );
}

export default Help;
